import type { Course } from "./store";
import { courseRunsThisWeek, teachingWeekForDate } from "./calendar-view.mjs";
export type WeekTimetableCourse = Course & { inactive?: boolean };
export type WeekCalendarRules = {
  semesterStart: string; noClassDates: string[];
  makeupDates: Record<string, { weekday: number; teachingDate: string; extraCourseIds?: string[] }>;
};

export type TimetableSlot = {
  weekday: number;
  periodStart: number;
  periodEnd: number;
  courses: WeekTimetableCourse[];
};

const isPersonalCourse = (course: Course) =>
  /^(mine:|personal:|edit:)/.test(course.id);

export function buildTotalTimetableCourses(source: Course[]): Course[] {
  const validCourses = source.filter((course) => {
    if (course.weekday < 1 || course.weekday > 7) return false;
    const match = course.period.match(/^(\d+)\s*[–-]\s*(\d+)$/);
    if (!match) return false;
    const start = Number(match[1]);
    const end = Number(match[2]);
    return start >= 1 && end <= 11 && end >= start;
  });
  const personalSlots = new Set(
    validCourses
      .filter(isPersonalCourse)
      .map((course) => `${course.weekday}|${course.period}|${course.title}`),
  );
  const uniqueCourses = new Map<string, Course>();
  for (const course of validCourses) {
    const slot = `${course.weekday}|${course.period}|${course.title}`;
    if (!isPersonalCourse(course) && personalSlots.has(slot)) continue;
    const key = `${slot}|${course.weeks || ""}|${course.room || ""}`;
    if (!uniqueCourses.has(key)) uniqueCourses.set(key, course);
  }
  return [...uniqueCourses.values()].sort((a, b) =>
    a.weekday - b.weekday ||
    Number(a.period.match(/^\d+/)?.[0] || 0) -
      Number(b.period.match(/^\d+/)?.[0] || 0),
  );
}

export function groupTotalTimetableSlots(courses: Course[]): TimetableSlot[] {
  const slots: TimetableSlot[] = [];
  for (const course of [...courses].sort(
    (a, b) => a.weekday - b.weekday ||
      Number(a.period.match(/^\d+/)?.[0] || 0) - Number(b.period.match(/^\d+/)?.[0] || 0),
  )) {
    const [periodStart, periodEnd] = course.period.split(/[–-]/).map(Number);
    const overlapping = slots.filter((slot) =>
      slot.weekday === course.weekday &&
      periodStart <= slot.periodEnd &&
      periodEnd >= slot.periodStart,
    );
    if (!overlapping.length) {
      slots.push({ weekday: course.weekday, periodStart, periodEnd, courses: [course] });
      continue;
    }
    const target = overlapping[0];
    target.periodStart = Math.min(target.periodStart, periodStart);
    target.periodEnd = Math.max(target.periodEnd, periodEnd);
    target.courses.push(course);
    for (const extra of overlapping.slice(1)) {
      target.periodStart = Math.min(target.periodStart, extra.periodStart);
      target.periodEnd = Math.max(target.periodEnd, extra.periodEnd);
      target.courses.push(...extra.courses);
      slots.splice(slots.indexOf(extra), 1);
    }
  }
  return slots.sort(
    (a, b) => a.weekday - b.weekday || a.periodStart - b.periodStart,
  );
}

/** Build a real-week grid from the complete semester, without splitting courses. */
export function buildWeekTimetableCourses(source: Course[], week: number, calendar: WeekCalendarRules,
  showInactive = false): WeekTimetableCourse[] {
  if (!Number.isInteger(week) || week < 1 || week > 30) return [];
  const [year, month, day] = calendar.semesterStart.split("-").map(Number);
  const start = Date.UTC(year, month - 1, day);
  const baseline = buildTotalTimetableCourses(source);
  const results: WeekTimetableCourse[] = [];
  for (let weekday = 1; weekday <= 7; weekday++) {
    const date = new Date(start + ((week - 1) * 7 + weekday - 1) * 86400000).toISOString().slice(0, 10);
    const makeup = calendar.makeupDates[date];
    if (calendar.noClassDates.includes(date) && !makeup) continue;
    const sourceWeek = makeup ? teachingWeekForDate(makeup.teachingDate, calendar.semesterStart) : week;
    const courseDay = makeup?.weekday || weekday;
    for (const course of baseline) {
      if (course.weekday !== courseDay) continue;
      const active = courseRunsThisWeek(course.weeks || "", sourceWeek)
        || Boolean(makeup?.extraCourseIds?.includes(course.id));
      const scheduledInRange = courseRunsThisWeek(
        (course.weeks || "").replace(/单周|双周/g, ""), sourceWeek);
      if (active || (showInactive && scheduledInRange)) {
        results.push({ ...course, weekday, inactive: !active });
      }
    }
  }
  return results;
}
